import { Adjustments } from "@/core/types"
import { Curve } from "@/core/types"
import { renderCpu, ColorSpaceCpu, RenderOptions } from "./renderer-cpu"
import { getGrayImage, getWeightedDiff, logColorMatch } from "./utils"
import { clamp } from "@/core/utils/math"
import Spline from "@/lib/spline"
import { interpolate } from "@/core/curves"
import { Sentry } from "@/telemetry/sentry"

export function getCurves(options: {
	concatedGammaAfter: Float32Array
	concatedSrgbBefore: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
	isBw: boolean
}) {
	const {
		concatedGammaAfter,
		concatedSrgbBefore,
		adjustments,
		baseRenderOptions,
		pixelWeights,
		isBw,
	} = options
	const curves: Partial<Adjustments> = {
		curvesAll: undefined,
		curvesRed: undefined,
		curvesGreen: undefined,
		curvesBlue: undefined,
	}

	// Get white curve
	const whiteCurve = getOptimalWhiteCurve(
		concatedGammaAfter,
		concatedSrgbBefore,
		adjustments,
		baseRenderOptions,
		pixelWeights,
	)
	const { newCurve, curveStrengthReduced } = getLimitedCurve(whiteCurve, 20)
	curves.curvesAll = newCurve

	// Get RGB curves
	if (!isBw && !curveStrengthReduced) {
		const [redCurve, greenCurve, blueCurve] = getOptimalRGBCurves(
			concatedGammaAfter,
			concatedSrgbBefore,
			{ ...adjustments, curvesAll: newCurve },
			baseRenderOptions,
			pixelWeights,
		)
		if (isRGBCurveStrong(redCurve, greenCurve, blueCurve)) {
			curves.curvesRed = redCurve
			curves.curvesGreen = greenCurve
			curves.curvesBlue = blueCurve
		}
	}

	return { curves, curveStrengthReduced }
}

function getAbsDiffForAdjustments(options: {
	beforeSrgb: Float32Array
	afterGamma: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
}): number {
	const { beforeSrgb, afterGamma, adjustments, baseRenderOptions, pixelWeights } = options
	const renderedGamma = renderCpu(beforeSrgb, adjustments, {
		...baseRenderOptions,
		outColorSpace: ColorSpaceCpu.gamma,
	})
	const diff = getWeightedDiff(afterGamma, renderedGamma, pixelWeights)
	return diff
}

function getCurvesNoise(curves: [Curve, Curve, Curve]): number {
	const noises = curves.map((curve) => getCurveNoise(curve))
	const average = noises.reduce((acc, noise) => acc + noise, 0) / 3
	const max = Math.max(...noises)
	return average * 0.5 + max * 0.5
}

function getCurveNoise(curve: Curve): number {
	if (curve.length < 2) return 0

	// Generate uniformly distributed points using spline interpolation
	const curveSize = 128
	const uniformPoints: [number, number][] = []
	const pointsSpline = new Spline(
		curve.map((p) => p?.[0] ?? 0),
		curve.map((p) => p?.[1] ?? 0),
	)

	let point = curve[0]?.[1] ?? 0
	for (let i = 0; i < curveSize; i++) {
		const x = (i / (curveSize - 1)) * 255
		point = interpolate(pointsSpline, x, point)
		uniformPoints.push([x, point])
	}

	// Calculate linear regression from all points
	let sumX = 0,
		sumY = 0,
		sumXY = 0,
		sumX2 = 0
	for (const [x, y] of uniformPoints) {
		sumX += x
		sumY += y
		sumXY += x * y
		sumX2 += x * x
	}
	const n = uniformPoints.length
	const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
	const intercept = (sumY - slope * sumX) / n

	// Calculate total squared difference from regression line
	let totalDiff = 0
	for (const [x, y] of uniformPoints) {
		const expectedY = slope * x + intercept
		totalDiff += (y - expectedY) ** 2
	}
	const noise1 = Math.sqrt(totalDiff / curveSize)

	const reducedUniformPoints = uniformPoints.filter((_, index) => index % 8 === 0)
	// Compute deviation from linear interpolation of nearby points
	const pointDeviations: number[] = []
	for (let i = 1; i < reducedUniformPoints.length - 1; i++) {
		const [x, y] = reducedUniformPoints[i] as [number, number]
		const [prevX, prevY] = reducedUniformPoints[i - 1] as [number, number]
		const [nextX, nextY] = reducedUniformPoints[i + 1] as [number, number]

		// Calculate expected Y using linear interpolation between neighboring points
		const expectedY = prevY + ((nextY - prevY) * (x - prevX)) / (nextX - prevX)

		// Deviation is the difference between actual y and expected y
		const deviation = Math.abs(y - expectedY)
		pointDeviations.push(deviation)
	}
	const noise2 = Math.sqrt(
		pointDeviations.reduce((acc, deviation) => acc + deviation ** 2, 0) /
			pointDeviations.length,
	)
	logColorMatch("[curve] noise", { noise1, noise2, final: noise1 * 0.3 + noise2 * 1.5 })

	return noise1 * 0.3 + noise2 * 1.5
}

export function getCurveStrength(curve: Curve): number {
	let maxDelta = 0
	for (const point of curve) {
		const y = point[1]
		const x = point[0]
		const delta = Math.abs(y - x)
		maxDelta = Math.max(maxDelta, delta)
	}
	return maxDelta
}

export function isRGBCurveStrong(curveRed: Curve, curveGreen: Curve, curveBlue: Curve): boolean {
	const threshold = 12
	const strengthRed = getCurveStrength(curveRed)
	const strengthGreen = getCurveStrength(curveGreen)
	const strengthBlue = getCurveStrength(curveBlue)
	logColorMatch("!![rgb curve] strength:", { strengthRed, strengthGreen, strengthBlue })
	return strengthRed > threshold || strengthGreen > threshold || strengthBlue > threshold
}

export function getOptimalWhiteCurve(
	concatedGammaAfter: Float32Array,
	concatedSrgbBefore: Float32Array,
	adjustments: Partial<Adjustments>,
	baseRenderOptions: RenderOptions,
	pixelWeights: number[],
): Curve {
	const concatedGammaCurrent = renderCpu(concatedSrgbBefore, adjustments, {
		...baseRenderOptions,
		outColorSpace: ColorSpaceCpu.gamma,
	})
	const grayLength = 256 * 3
	const whiteCurveConcated = getWhiteCurve(concatedGammaCurrent, concatedGammaAfter)
	const whiteCurveGray = getWhiteCurve(
		concatedGammaCurrent.slice(0, grayLength),
		concatedGammaAfter.slice(0, grayLength),
	)

	const srgbBefore = concatedSrgbBefore.slice(grayLength)
	const gammaAfter = concatedGammaAfter.slice(grayLength)

	const diffConcated = getAbsDiffForAdjustments({
		beforeSrgb: srgbBefore,
		afterGamma: gammaAfter,
		adjustments: { ...adjustments, curvesAll: whiteCurveConcated },
		baseRenderOptions,
		pixelWeights,
	})
	const diffGray = getAbsDiffForAdjustments({
		beforeSrgb: srgbBefore,
		afterGamma: gammaAfter,
		adjustments: { ...adjustments, curvesAll: whiteCurveGray },
		baseRenderOptions,
		pixelWeights,
	})
	const noiseConcated = getCurveNoise(whiteCurveConcated)
	const noiseGray = getCurveNoise(whiteCurveGray)
	logColorMatch("!![curve] white:", {
		noiseConcated,
		diffConcated,
		noiseGray,
		diffGray,
	})

	if (diffConcated < diffGray && (noiseConcated <= noiseGray + 1.0 || noiseConcated <= 1.5)) {
		logColorMatch("!![curve] white: finally use curveConcated!!!")
		return whiteCurveConcated
	} else {
		return whiteCurveGray
	}
}

export function getOptimalRGBCurves(
	concatedGammaAfter: Float32Array,
	concatedSrgbBefore: Float32Array,
	adjustments: Partial<Adjustments>,
	baseRenderOptions: RenderOptions,
	pixelWeights: number[],
): [Curve, Curve, Curve] {
	const concatedGammaCurrent = renderCpu(concatedSrgbBefore, adjustments, {
		...baseRenderOptions,
		outColorSpace: ColorSpaceCpu.gamma,
	})
	const grayLength = 256 * 3
	const curveConcated = getRGBCurves(concatedGammaCurrent, concatedGammaAfter)
	const curveGray = getRGBCurves(
		concatedGammaCurrent.slice(0, grayLength),
		concatedGammaAfter.slice(0, grayLength),
	)

	const srgbBefore = concatedSrgbBefore.slice(grayLength)
	const gammaAfter = concatedGammaAfter.slice(grayLength)

	const diffConcated = getAbsDiffForAdjustments({
		beforeSrgb: srgbBefore,
		afterGamma: gammaAfter,
		adjustments: {
			...adjustments,
			curvesRed: curveConcated[0],
			curvesGreen: curveConcated[1],
			curvesBlue: curveConcated[2],
		},
		baseRenderOptions,
		pixelWeights,
	})
	const diffGray = getAbsDiffForAdjustments({
		beforeSrgb: srgbBefore,
		afterGamma: gammaAfter,
		adjustments: {
			...adjustments,
			curvesRed: curveGray[0],
			curvesGreen: curveGray[1],
			curvesBlue: curveGray[2],
		},
		baseRenderOptions,
		pixelWeights,
	})
	const noiseConcated = getCurvesNoise(curveConcated)
	const noiseGray = getCurvesNoise(curveGray)
	logColorMatch("!![curve] rgb:", {
		noiseConcated,
		diffConcated,
		noiseGray,
		diffGray,
	})

	if (diffConcated < diffGray && (noiseConcated <= noiseGray + 0.2 || noiseConcated <= 1.0)) {
		logColorMatch("!![curve] rgb: finally use curveConcated!!!")
		return curveConcated
	} else {
		return curveGray
	}
}

function assureMonotonic(curve: Curve): Curve {
	// Ensure y values are strictly increasing
	for (let i = 1; i < curve.length; i++) {
		const y = curve[i]?.[1] ?? 0
		const x = curve[i]?.[0] ?? 0
		const yPrev = curve[i - 1]?.[1] ?? 0
		if (y <= yPrev + 3) {
			const y = clamp(yPrev + 4, 0, 255)
			curve[i] = [x, y]
		}
	}

	return curve
}

// function polyfit(xs: number[], ys: number[], degree: number): number[] {
// 	const n = xs.length
// 	const sums: number[] = new Array(2 * degree + 1).fill(0)
// 	const a: number[][] = Array.from(
// 		{ length: degree + 1 },
// 		() => Array(degree + 1).fill(0) as number[],
// 	)

// 	const b: number[] = new Array(degree + 1).fill(0)

// 	// Build sums of x^i
// 	for (let i = 0; i < n; i++) {
// 		let temp = 1
// 		for (let j = 0; j < 2 * degree + 1; j++) {
// 			sums[j] = (sums[j] ?? 0) + temp
// 			temp *= xs[i] ?? 0
// 		}
// 	}

// 	// Build normal equations
// 	for (let i = 0; i <= degree; i++) {
// 		for (let j = 0; j <= degree; j++) {
// 			;(a[i] ?? [])[j] = sums[i + j] ?? 0
// 		}
// 	}

// 	// Build right-hand side
// 	for (let i = 0; i < n; i++) {
// 		let temp = 1
// 		for (let j = 0; j <= degree; j++) {
// 			b[j] = (b[j] ?? 0) + (ys[i] ?? 0) * temp
// 			temp *= xs[i] ?? 0
// 		}
// 	}

// 	// Solve using Gaussian elimination
// 	for (let i = 0; i <= degree; i++) {
// 		const pivot = (a[i] ?? [])[i] ?? 0
// 		for (let j = i; j <= degree; j++) {
// 			;(a[i] ?? [])[j] = ((a[i] ?? [])[j] ?? 0) / pivot
// 		}
// 		b[i] = (b[i] ?? 0) / pivot

// 		for (let j = 0; j <= degree; j++) {
// 			if (i !== j) {
// 				const factor = (a[j] ?? [])[i] ?? 0
// 				for (let k = i; k <= degree; k++) {
// 					;(a[j] ?? [])[k] = ((a[j] ?? [])[k] ?? 0) - factor * ((a[i] ?? [])[k] ?? 0)
// 				}
// 				b[j] = (b[j] ?? 0) - factor * (b[i] ?? 0)
// 			}
// 		}
// 	}

// 	return b // Returns coefficients
// }

// function evaluatePolynomial(coefficients: number[], x: number): number {
// 	let result = 0
// 	for (let i = 0; i < coefficients.length; i++) {
// 		result += (coefficients[i] ?? 0) * Math.pow(x, i)
// 	}
// 	return result
// }

function simplifyControlPoints(curve: Curve, tolerance: number): Curve {
	if (curve.length <= 3) return curve

	const simplified: Curve = [curve[0] as [number, number]]

	let lastSkipped = -10
	for (let i = 1; i < curve.length - 1; i++) {
		const prev = simplified[simplified.length - 1]
		const curr = curve[i]
		const next = curve[i + 1]

		if (!prev || !curr || !next) continue
		if (i === lastSkipped + 1) {
			// add the point if the last skipped point is the previous one
			simplified.push(curr)
			continue
		}

		// Calculate expected Y value if the point was on a straight line
		const x = curr[0]
		const expectedY = prev[1] + ((next[1] - prev[1]) * (x - prev[0])) / (next[0] - prev[0])

		// If the actual Y is close to the expected Y (within tolerance), skip this point
		if (Math.abs(curr[1] - expectedY) > tolerance) {
			simplified.push(curr)
		} else {
			lastSkipped = i
		}
	}

	// Always include the last point
	simplified.push(curve[curve.length - 1] as [number, number])

	return simplified
}

function getCurve(input: Float32Array, output: Float32Array): Curve {
	// Create points array and normalize to [0,1]
	const points: [number, number][] = []
	for (let i = 0; i < input.length; i++) {
		const x = input[i] ?? 0
		const y = output[i] ?? 0
		if (!isNaN(x) && !isNaN(y)) {
			points.push([x, y])
		}
	}

	// Sort points by x value
	points.sort((a, b) => a[0] - b[0])

	// Resample points to get even distribution
	const numSamples = 64 // Choose appropriate number of samples
	const resampledPoints: [number, number][] = []

	for (let i = 0; i < numSamples; i++) {
		const targetX = i / (numSamples - 1)

		// Find surrounding points
		const idx = points.findIndex((p) => p[0] >= targetX)
		if (idx === -1 || idx === 0) {
			const X_RANGE = 0.08 // Use points within 8% range for regression
			const firstX = points[0]?.[0] ?? 0
			const lastX = points[points.length - 1]?.[0] ?? 1
			const usePoints =
				idx === 0
					? points.filter((p) => p[0] <= firstX + X_RANGE) // Points near start
					: points.filter((p) => p[0] >= lastX - X_RANGE) // Points near end

			if (usePoints.length >= 2) {
				// Calculate slope using linear regression
				let sumX = 0,
					sumY = 0,
					sumXY = 0,
					sumX2 = 0
				for (const [x, y] of usePoints) {
					sumX += x
					sumY += y
					sumXY += x * y
					sumX2 += x * x
				}
				const n = usePoints.length
				let slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
				const intercept = (sumY - slope * sumX) / n
				slope = Math.max(slope, 0)

				const y = slope * targetX + intercept
				const clampedY = clamp(y, 0, 1)
				resampledPoints.push([targetX, clampedY])
			} else {
				// Fallback if not enough points
				const y = idx === 0 ? (points[0]?.[1] ?? 0) : (points[points.length - 1]?.[1] ?? 1)
				resampledPoints.push([targetX, y])
			}
		} else {
			// Interpolate using nearby points
			const window = 0.03 // Size of window to consider

			// Find points within the window
			const nearbyPoints = points.filter(
				(p) => p[0] >= targetX - window && p[0] <= targetX + window,
			)

			if (nearbyPoints.length >= 2) {
				// Linear regression
				let sumX = 0,
					sumY = 0,
					sumXY = 0,
					sumX2 = 0
				for (const [x, y] of nearbyPoints) {
					sumX += x
					sumY += y
					sumXY += x * y
					sumX2 += x * x
				}
				const n = nearbyPoints.length

				// Calculate slope and intercept
				const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
				const intercept = (sumY - slope * sumX) / n

				// Calculate interpolated y value
				const y = slope * targetX + intercept
				resampledPoints.push([targetX, clamp(y, 0, 1)])
			} else {
				// Fallback to linear interpolation using two nearest points
				const p1 = points[idx - 1] as [number, number]
				const p2 = points[idx] as [number, number]
				const t = (targetX - p1[0]) / Math.max(0.00001, p2[0] - p1[0])
				const y = p1[1] + t * (p2[1] - p1[1])
				resampledPoints.push([targetX, clamp(y, 0, 1)])
			}
		}
	}

	// Ensure monotonicity of input points
	let maxY = resampledPoints[0]?.[1] ?? 0
	for (let i = 1; i < resampledPoints.length; i++) {
		maxY = Math.max(maxY, resampledPoints[i]?.[1] ?? 0)
		resampledPoints[i] = [resampledPoints[i]?.[0] ?? 0, maxY]
	}

	// Prevsiouly used polynomial fit, but it tends to be unstable (oscillating)
	// const coefficients = polyfit(
	// 	resampledPoints.map((p) => p[0]),
	// 	resampledPoints.map((p) => p[1]),
	// 	5, // degree
	// )

	// Fit spline
	const spline = new Spline(
		resampledPoints.map((p) => p[0]),
		resampledPoints.map((p) => p[1]),
	)

	// Generate final curve points
	const targetX = [0, 16, 32, 48, 64, 96, 128, 160, 192, 208, 224, 240, 255]
	let curve: Curve = []
	for (const x of targetX) {
		const xNorm = x / 255
		const y = interpolate(spline, xNorm, xNorm) // evaluatePolynomial(coefficients, xNorm)
		curve.push([x, clamp(Math.round(y * 255), 0, 255)])
	}

	// logColorMatch("[curve] resampled points", resampledPoints)
	// logColorMatch("[curve] curve", curve)

	curve = assureMonotonic(curve)
	curve = simplifyControlPoints(curve, 1)

	return curve
}

function getWhiteCurve(before: Float32Array, after: Float32Array): Curve {
	const grayInput = getGrayImage(before)
	const grayOutput = getGrayImage(after)
	return getCurve(grayInput, grayOutput)
}

function getRGBCurves(
	before: Float32Array,
	after: Float32Array,
	strength: number = 1,
): [Curve, Curve, Curve] {
	const curves: [Curve, Curve, Curve] = [[], [], []]

	for (let channel = 0; channel < 3; channel++) {
		const channelInput = getChannel(before, channel)
		const channelOutput = getChannel(after, channel)
		let curve = getCurve(channelInput, channelOutput)

		if (strength !== 1) {
			curve = curve.map(([x, y]) => [x, clamp(x + (y - x) * strength, 0, 255)])
		}

		curves[channel] = curve
	}

	return curves
}

// Helper functions
function getChannel(data: Float32Array, channel: number): Float32Array {
	return data.filter((_, i) => i % 3 === channel)
}

export function getLimitedCurve(
	curve: Curve,
	maxDelta: number = 20,
): { newCurve: Curve; curveStrengthReduced: boolean } {
	let scale = 1
	for (const [x, y] of curve) {
		const delta = Math.abs(y - x)
		if (delta > maxDelta) {
			scale = Math.min(scale, maxDelta / delta)
		}
	}
	logColorMatch("[curve] limited curve", { scale, maxDelta })
	return {
		newCurve: curve.map(([x, y]) => [x, x + (y - x) * scale]),
		curveStrengthReduced: scale !== 1,
	}
}

function isValidProfileCurve(profileCurve: Curve): boolean {
	if (profileCurve.length < 3) {
		return false
	}
	// check NaN
	if (profileCurve.some((point) => isNaN(point[0]) || isNaN(point[1]))) {
		return false
	}

	// check if the first and last points are very close to [0, 0] and [255, 255]
	if (Math.abs(profileCurve[0]?.[0] ?? 0) > 0.1 || Math.abs(profileCurve[0]?.[1] ?? 0) > 0.1) {
		return false
	}
	if (
		Math.abs((profileCurve[profileCurve.length - 1]?.[0] ?? 0) - 255) > 0.1 ||
		Math.abs((profileCurve[profileCurve.length - 1]?.[1] ?? 0) - 255) > 0.1
	) {
		return false
	}

	// check if all points are in range [0, 255]
	for (const point of profileCurve) {
		if (point[0] < 0 || point[0] > 255 || point[1] < 0 || point[1] > 255) {
			return false
		}
	}

	// check if the curve is monotonic
	for (let i = 1; i < profileCurve.length; i++) {
		const x1 = profileCurve[i - 1]?.[0] ?? 0
		const y1 = profileCurve[i - 1]?.[1] ?? 0
		const x2 = profileCurve[i]?.[0] ?? 0
		const y2 = profileCurve[i]?.[1] ?? 0
		if (x1 > x2 || y1 > y2) {
			return false
		}
	}

	return true
}

export function getValidProfileCurve(profileCurve: Curve | null): Curve | null {
	if (profileCurve) {
		if (!isValidProfileCurve(profileCurve)) {
			console.warn("Invalid profile curve", profileCurve)
			Sentry.captureMessage("Invalid profile curve", {
				level: "warning",
				extra: {
					profileCurve,
				},
			})
			profileCurve = null
		}
	}
	return profileCurve
}

/**
 * Softly bring down lifted mid/upper curve only in x ∈ [regionStart, regionEnd],
 * capping the peak to y <= x + maxExcess and smoothly reducing neighbors.
 * Keeps the curve monotonic non-decreasing.
 */
export function softenCurveUpperMid(
	adjustments: Partial<Adjustments>,
	maxExcess = 5,
	regionStart = 110,
	regionEnd = 210,
	radius = 100,
): Partial<Adjustments> {
	const points = adjustments.curvesAll
	if (!points?.length) return adjustments
	const pts: Curve = [...points]

	// Indices in the target region
	const idxInRegion: number[] = []
	for (let i = 0; i < pts.length; i++) {
		const p = pts[i] as [number, number]
		const x = p[0]
		if (x >= regionStart && x <= regionEnd) idxInRegion.push(i)
	}
	if (idxInRegion.length === 0) return adjustments

	// Find peak excess in region
	let peakIdx = -1
	let peakDelta = 0
	for (const i of idxInRegion) {
		const p = pts[i] as [number, number]
		const x = p[0]
		const y = p[1]
		const delta = y - (x + maxExcess)
		if (delta > peakDelta) {
			peakDelta = delta
			peakIdx = i
		}
	}
	if (peakIdx === -1 || peakDelta <= 0) return adjustments

	const peakPoint = pts[peakIdx] as [number, number]
	const peakX = peakPoint[0]

	const adjusted: Curve = pts.map(([x, y]) => {
		if (x < regionStart || x > regionEnd) return [x, y] // untouched outside region

		const dist = Math.abs(x - peakX)
		const weight = Math.max(0, 1 - dist / radius)

		const baseReduction = peakDelta * weight
		let newY: number
		if (y > x + maxExcess * weight) {
			newY = Math.max(x + maxExcess * weight, y - baseReduction)
		} else {
			newY = Math.max(Math.min(x, y), y - baseReduction * 0.3)
		}

		newY = Math.min(255, Math.max(0, newY))
		return [x, newY]
	})

	// Enforce monotonic non-decreasing y across the whole curve
	for (let i = 1; i < adjusted.length; i++) {
		const prev = adjusted[i - 1] as [number, number]
		const cur = adjusted[i] as [number, number]
		if (cur[1] < prev[1]) {
			adjusted[i] = [cur[0], prev[1] + 2]
		}
	}

	// determine contrast change based on the peak delta
	const contrastDelta = Math.min(peakDelta / 100, 0.15)
	adjustments.contrast = Math.min((adjustments.contrast ?? 0) + contrastDelta, 1)
	logColorMatch("[softenCurveUpperMid]", {
		contrastDelta,
		peakDelta,
		peakIdx,
		adjusted,
		original: points,
	})

	return { ...adjustments, curvesAll: adjusted }
}
